import { useGetEDocPlan } from '@/context/services/e_doc_plan/EDocPlan.service';
import { usePictureInPictureModeContext } from '@/context/InvoiceReports';
import {
  ActiveFileType,
  TAB_KEYS,
  usePaymentDetail,
} from '@/components/InvoiceReportDetailDrawer/context/PaymentDetailContext';
import { usePdfPreview } from '@/components/InvoiceReportDetailDrawer/hooks/usePdfPreview';
import {
  ApprovingInvoiceReportFormInputResponse,
  ApprovingInvoiceReportPaymentInfoResponse,
  EDocMetaDatum,
  reportFormInputType,
} from 'ap-openapi';
import { useCallback, useEffect, useMemo } from 'react';

type ScanDecisionType = 'ok' | 'warning' | 'error' | undefined;

// Helper function to calculate scan decision
const getScanDecision = (
  eDocMetaDatum: EDocMetaDatum | undefined,
  alertType: 'warning' | 'error' | undefined,
  isEDocApReceiptTypeEnabled = true
): ScanDecisionType => {
  if (!isEDocApReceiptTypeEnabled && alertType) {
    return undefined;
  }

  if (!eDocMetaDatum) {
    return undefined;
  }

  const isDepthRequirementsMet = eDocMetaDatum.is_satisfy_depth_requirements;
  const isResolutionRequirementMet =
    eDocMetaDatum.is_satisfy_resolution_requirement;

  return isDepthRequirementsMet && isResolutionRequirementMet
    ? 'ok'
    : alertType;
};

// Hook that provides a function to select any invoice file item
export const useInvoiceFileSelector = () => {
  const baseURL = location.origin;
  const { setInvoiceFileId } = usePictureInPictureModeContext();
  const { setActiveFile, setPdfViewerProps } = usePaymentDetail();
  const {
    normalizeInvoiceFile,
    createUnifiedPreviewProps,
    selectAndShowFile,
    getInvoiceFileForDisplay,
  } = usePdfPreview({
    baseURL,
  });
  const { apReceiptTypeAlertType } = useGetEDocPlan();

  // Returns a function that can select any invoice file item
  const createInvoiceFileSelector = useCallback(
    (item: ApprovingInvoiceReportFormInputResponse) => {
      const processedFile = getInvoiceFileForDisplay(item);

      if (!processedFile?.id) {
        selectAndShowFile(
          '',
          ActiveFileType.INVOICE,
          {},
          TAB_KEYS.ATTACHED_INVOICE
        );
        setInvoiceFileId(null);
        setActiveFile(null);
        setPdfViewerProps(undefined);
        return;
      }

      const unifiedDoc = normalizeInvoiceFile(item);
      if (!unifiedDoc) return;

      const receiptType = processedFile.receipt_type;
      const eDocMeta = processedFile.e_doc_meta_datum;

      const scanDecision = eDocMeta
        ? getScanDecision(eDocMeta, apReceiptTypeAlertType, true)
        : undefined;

      const showTimestamp =
        !!eDocMeta && processedFile.timestamp_status === 'done';

      const pdfViewerProps = createUnifiedPreviewProps(
        unifiedDoc,
        receiptType,
        eDocMeta,
        scanDecision,
        showTimestamp
      );

      if (pdfViewerProps) {
        selectAndShowFile(
          processedFile.id,
          ActiveFileType.INVOICE,
          pdfViewerProps,
          TAB_KEYS.ATTACHED_INVOICE
        );
        setInvoiceFileId(processedFile.id);
      }
    },
    [
      getInvoiceFileForDisplay,
      normalizeInvoiceFile,
      createUnifiedPreviewProps,
      selectAndShowFile,
      apReceiptTypeAlertType,
      setInvoiceFileId,
      setActiveFile,
      setPdfViewerProps,
    ]
  );

  return { createInvoiceFileSelector };
};

// Hook for managing invoice file selection and tab synchronization
export const useInvoiceFiles = (
  item: ApprovingInvoiceReportFormInputResponse,
  baseURL: string
) => {
  const { createInvoiceFileSelector } = useInvoiceFileSelector();
  const { getInvoiceFileForDisplay } = usePdfPreview({ baseURL });
  const { activeTab, activeFile } = usePaymentDetail();

  // Get processed invoice file
  const processedFile = useMemo(() => {
    return getInvoiceFileForDisplay(item);
  }, [item, getInvoiceFileForDisplay]);

  // Create selection function for this invoice file
  const selectInvoiceFile = useCallback(() => {
    if (item) {
      createInvoiceFileSelector(item);
    }
  }, [item, createInvoiceFileSelector]);

  // Automatically set invoice file as active on mount (default behavior)
  useEffect(() => {
    if (processedFile?.id && !activeFile) {
      // Only set as active if no file is currently active
      selectInvoiceFile();
    }
  }, [processedFile?.id, activeFile, selectInvoiceFile]);

  // Watch for tab changes and activate invoice file when invoice tab is selected
  useEffect(() => {
    if (activeTab === TAB_KEYS.ATTACHED_INVOICE && processedFile?.id) {
      selectInvoiceFile();
    }
  }, [activeTab, processedFile?.id, selectInvoiceFile]);

  return {
    processedFile,
    selectInvoiceFile,
    activeFile,
  };
};

// Hook for selecting the first invoice file from payment data
export const useFirstInvoiceFileSelection = (
  data: ApprovingInvoiceReportPaymentInfoResponse | undefined
) => {
  const { createInvoiceFileSelector } = useInvoiceFileSelector();

  // Find the first invoice file input
  const firstInvoiceFileInput = useMemo(() => {
    if (!data?.report_form_inputs || data.report_form_inputs.length === 0) {
      return null;
    }
    return data.report_form_inputs.find(
      (input) =>
        input?.type === reportFormInputType.ApReportFormInputInvoiceFileField
    );
  }, [data?.report_form_inputs]);

  // Function to select the first invoice file
  const selectFirstInvoiceFile = useCallback(() => {
    if (firstInvoiceFileInput) {
      createInvoiceFileSelector(firstInvoiceFileInput);
    }
  }, [firstInvoiceFileInput, createInvoiceFileSelector]);

  return { selectFirstInvoiceFile, firstInvoiceFileInput };
};
